<template>
  <div class="card text-decoration-none mb-4" itemscope itemtype="https://schema.org/Hotel">
    <div class="card-header p-0">


      <figure class="position-relative slider-photo mb-0">
        <ClientOnly>
          <ImageSlider :item="accommodation" :key="`image_slider_square_${accommodation.id}`"
                       :hrefLink="accommodation.getLinkWithSlash(activeLocale.code, activeCountry.code)"
                       @hrefAction="trackRedirect"/>
          <template #fallback>
            <picture>
              <PartsSrcSetImage v-if="accommodation.hasImage()" :image="accommodation.getTopImage()" :t_type="'thumb_m'"
                                :type="'webp'" media="(min-width: 1500px)"/>
              <PartsSrcSetImage v-if="accommodation.hasImage()" :image="accommodation.getTopImage()" :t_type="'thumbw'"
                                :type="'webp'"/>
              <PartsSrcSetImage v-if="accommodation.hasImage()" :image="accommodation.getTopImage()" :t_type="'thumbw'"
                                :type="'jpeg'"/>
              <img v-if="accommodation.hasImage()" :src="accommodation.getTopImage().getTyrionSrc('thumbw')"
                   :alt="accommodation.getTopImage().title " class="big-photo img-cover" loading="lazy"
                   :width="accommodation.getTopImage().seoWidth('thumb')"
                   itemprop="photo"
                   :height="accommodation.getTopImage().seoHeight('thumb')">
              <img v-else src="~/assets/img/blank.jpg" :alt="accommodation.title" class="img-cover" loading="lazy"
                   width="600" height="337">
            </picture>
            <figcaption
                :class="{'is_last_minute': showLastMinutes && accommodation.has_last_minute, 'is_top': accommodation.is_top}">
              <div class="lastminute p-1 px-3" v-if="showLastMinutes && accommodation.has_last_minute">lastminute</div>
              <div class="top p-1 px-3" v-if="accommodation.is_top">top</div>
            </figcaption>
          </template>
        </ClientOnly>
      </figure>
    </div>
    <div class="card-body pb-0">
      <NuxtLink :to="accommodation.getLinkWithSlash(activeLocale.code,activeCountry.code)"
                class="text-decoration-none h-100" :title="accommodation.title"
                @click.native="trackRedirect">


        <h3 class="card-title" itemprop="name">{{ accommodation.title }}</h3>
        <div class="info-text">
          <p class="capacity">{{ accommodation.max_hosts_capacity }}
            {{ $t($vysklonujSlovo('host', accommodation.max_hosts_capacity, 'ia', 'i')) }}
            <span>,</span>
          </p>

          <p class="bedroom"
             v-if="accommodation.getTotalRoomsCount('bedroom') > 0">{{ accommodation.getTotalRoomsCount('bedroom') }}
            {{ $t($vysklonujSlovo('izba', accommodation.getTotalRoomsCount('bed_rooms'), 'y', 'ieb')) }}
            <span>,</span>
          </p>

          <p class="bathroom" v-if="accommodation.getTotalRoomsCount('bathroom') > 0">
            {{ accommodation.getTotalRoomsCount('bathroom') }}
            {{ $t($vysklonujSlovo('kupelna', accommodation.getTotalRoomsCount('bathroom'), 'e', 'i')) }}
            <span>,</span>
          </p>

          <p>{{ $t(accommodation.category) }},</p>

          <meta itemprop="url"
                :content="useRuntimeConfig().public.WEB_URL + accommodation.getLinkWithSlash(activeLocale.code,activeCountry.code)">
          <p class="address" itemprop="address" itemscope itemtype="https://schema.org/PostalAddress"
             v-if="accommodation.location && accommodation.location.city">
            <span>{{ accommodation.location.city.title }}, {{ accommodation.location.country.title }}, </span>
            <meta itemprop="postalCode" :content="accommodation.location.city.postcode"/>
            <meta itemprop="addressLocality" :content="accommodation.location.city.title"/>
            <meta itemprop="addressRegion" :content="accommodation.location.region.title"/>
            <meta itemprop="addressCountry" :content="accommodation.location.country.title"/>
          </p>
        </div>
      </NuxtLink>
    </div>
    <div class="card-footer pt-0 pb-3">
      <p class="mb-0 price"
         v-if="showLastMinutes && accommodation.last_minutes && accommodation.last_minutes.length > 0">
        {{
          Math.round(Number(accommodation.last_minutes[0].curr_prices.EUR * accommodation.last_minutes[0].duration_days))
        }}{{ currency.mark }}
        <span class="price-description">za {{ accommodation.last_minutes[0].duration_days }} {{
            $t($vysklonujSlovo('noc', accommodation.last_minutes[0].duration_days, 'i', 'och'))
          }}</span>
      </p>

      <p class="mb-0 price" v-else-if="accommodation.price_from != 9999 && accommodation.price_from > 0"
         itemprop="priceRange">
        <span class="d-inline-block price-description">{{ $t('from').toUpperCase() }}</span>
        {{ formatPrice(accommodation.price_from, activeLocale.code) }}
      </p>


    </div>
  </div>
</template>

<script setup lang="ts">
import {PropType, computed} from "vue";
import Accommodation from "../../../../../lib/models/Accommodation";
import {useNuxtApp, useRuntimeConfig} from "#app";
import ImageSlider from "../acc_list_main/acc_list_main_accommodation_item_image_slider";
import {formatPrice, switchCurrencyMark} from "../../../../../lib/functions";

const {$isDesktop} = useNuxtApp();
const {$vysklonujSlovo} = useNuxtApp();
const props = defineProps({
  activeLocale: Object as PropType<{ code: string }>,
  activeCountry: Object as PropType<{ code: string }>,
  accommodation: Object as PropType<Accommodation>,
  widgetName: String,
  currency: Object as PropType<{ code: String, name: String, mark: String }>,
  GATrackEvent: Function,
  showLastMinutes: Boolean
});


const hrefTarget = computed(() => {
  return $isDesktop() ? "_blank" : "_self"
})


const trackRedirect = () => {
  if (props.GATrackEvent && props.widgetName) {
    props.GATrackEvent({
      event: 'open_accommodation',
      action: 'redirect',
      label: 'Redirect to accommodation from list item',
      value: props.accommodation.link,
      widget: props.widgetName,
      items: [
        {
          item_id: props.accommodation.id,
          item_name: props.accommodation.title,
          item_category: props.accommodation.category,
          item_variant: props.accommodation.location.tourist_region ? props.accommodation.location.tourist_region.code : null
        }
      ]
    }, 0);
  }
}
</script>
